import React from "react";
import { observer } from "mobx-react-lite";
import { enhancedContentSourceLocation } from "../../../lib/ContentSource";
import { ReportSection, ReportSectionHeader, Literal, IndentedBlock, ReportSubSectionHeader, InferredTypeName, SearchDetails, Report, ReportTitle, ReportSubSubSectionHeader, SizeAdjustedLowercaseLiteral, SearchResultFoundLine, SearchResultEncodingFoundSpan, InferredTypeNameSpan, SearchTermFoundSpan, SearchTermFoundLine } from "../common-styles";
import { SearchResultDetailsView } from "./SearchResultDetailsView";
import { RecordingSearchState } from "../RecordingSearchState";
import { BehaviorsObservedWhenDomainOnCallStack } from "../InstrumentationReportingState";
import { TrackingSummaryReportState } from "../getTrackingSummaryReportState";
import { KnownTrackerView } from "./KnownTrackerView";

type SearchTermsFound = RecordingSearchState["searchResultsBySourceRegisteredDomain"][number]["destinations"][number]["searchTerms"];
type EncodingResults = SearchTermsFound[number]["encodings"][number];
export const  EncodingDetailsView = ({encodingResults, displayJson}: {
	encodingResults: EncodingResults, displayJson?: (json: unknown) => void;
}) => {
	const {encodingString, eventsAndResults} = encodingResults;
	// If there are four or fewer events, show them all.
	// If there are more than four events, show the first three and expand the remaining ones.
	const numberOfEventsToAlwaysShow = eventsAndResults.length <= 4 ? eventsAndResults.length : 3;
	const eventsToAlwaysShow = eventsAndResults.slice(0, numberOfEventsToAlwaysShow);
	const eventsToBeRevealedWhenExpanded = eventsAndResults.slice(numberOfEventsToAlwaysShow);
	return (
		<IndentedBlock key={encodingString}>
			<SearchResultFoundLine><SearchResultEncodingFoundSpan>{encodingString}</SearchResultEncodingFoundSpan>&#32;found at:</SearchResultFoundLine>
				{eventsToAlwaysShow.map(({ event, searchResult }) => (
						<SearchResultDetailsView
							key={`${event.eventIndex}\0${searchResult.encodedSearchTerm}\0${searchResult.position}\0${enhancedContentSourceLocation(searchResult)}`}
							{...{ event, searchResult, displayJson }} />
					))}
				{eventsToBeRevealedWhenExpanded.length === 0 ? null : (
					<SearchDetails key={encodingString}>
						<summary>and&#32;{eventsToBeRevealedWhenExpanded.length}&#32;additional occurrences</summary>
						{eventsToBeRevealedWhenExpanded.map(({ event, searchResult }) => (
							<SearchResultDetailsView
								key={`${event.eventIndex}\0${searchResult.encodedSearchTerm}\0${searchResult.position}\0${enhancedContentSourceLocation(searchResult)}`}
								{...{ event, searchResult, displayJson }} />
						))}
					</SearchDetails>
				)}
		</IndentedBlock>
	);
}


const DomainSearchResultsView = observer( (
	{
		searchTerms,
		displayJson,
}: {
	displayJson?: (json: unknown) => void;
	searchTerms?: SearchTermsFound;
}) => {
	if (searchTerms == null) return null;
	return (<>
		<ReportSubSectionHeader>Data Transmitted</ReportSubSectionHeader>
		{searchTerms.map(({ originalSearchTerm, encodings, inferredTypeNames }) => (
		<IndentedBlock key={originalSearchTerm}>
			<SearchTermFoundLine>
				“<SearchTermFoundSpan>{originalSearchTerm}</SearchTermFoundSpan>”
				{inferredTypeNames.length === 0 ? null : (<>
					&#32;labeled as&#32;
					<InferredTypeNameSpan>
						{inferredTypeNames.map( (inferredTypeName, index) => (
							<span key={inferredTypeName}>
								{index === 0 ? null : (<>,&#32;</>)}
								<InferredTypeName>{inferredTypeName}</InferredTypeName>
							</span>
						))}
					</InferredTypeNameSpan>
				</>)}
			</SearchTermFoundLine>
			{encodings.map( encodingResults => (<EncodingDetailsView key={encodingResults.encodingString} {...{encodingResults, displayJson}} />))}
		</IndentedBlock>
		))
	}</>)
});

export const DomainInstrumentationReport = observer ( ({
	behaviorsObservedAtCodeLocation
}: {
	behaviorsObservedAtCodeLocation?: BehaviorsObservedWhenDomainOnCallStack
}) => {
	if (behaviorsObservedAtCodeLocation == null) return null;
	const {geolocationPositionsObserved, globalKeystrokesRead, eventListenersOnGlobalTags, globalPropertiesRead, canvasRenderingContexts, apiConstants
	} = behaviorsObservedAtCodeLocation;
	if (geolocationPositionsObserved == null && globalKeystrokesRead == null && eventListenersOnGlobalTags == null && globalPropertiesRead == null) return null;
	return (<>
		<ReportSubSectionHeader>Code behavior</ReportSubSectionHeader>
		<IndentedBlock>
			{ geolocationPositionsObserved == null || geolocationPositionsObserved.length == 0 ? null : (<>
				<ReportSubSubSectionHeader>Geolocation</ReportSubSubSectionHeader>
				<IndentedBlock>{ geolocationPositionsObserved.map( ({position, eventIndex}) => 
					<div key={eventIndex}>
						<Literal>{position.coords.longitude}</Literal>,&#32;
						<Literal>{position.coords.latitude}</Literal> {position.coords.heading}
					</div>)
				}</IndentedBlock>
			</>)}
			{ (canvasRenderingContexts == null || canvasRenderingContexts.length === 0) && apiConstants == null ? null : (<>
				<ReportSubSubSectionHeader>APIs accessed (often used for fingerprinting)</ReportSubSubSectionHeader>
				<IndentedBlock>{
					canvasRenderingContexts == null ? null : (<>
						{ /* FIXME */ }
						<div><Literal>HTMLCanvasElement.getContext()</Literal> called with <Literal>{ 
							canvasRenderingContexts.join(", ")}</Literal></div>
					</>)
					}{
						apiConstants == null ? null : 
							Object.entries(apiConstants).map( ([prop, value]) => (
							<div key={prop}><Literal>{prop}={value}</Literal></div>
						))
				}
				</IndentedBlock>
			</>)}
			{ globalKeystrokesRead == null ? null : (<>
				<ReportSubSubSectionHeader>Keystrokes read (by listening to global DOM elements)</ReportSubSubSectionHeader>
				<IndentedBlock>{ Object.entries(globalKeystrokesRead).map( ([event, keyStrokes]) => 
					<div key={event}>
						<Literal>{event}</Literal>:
						<Literal>{
							keyStrokes.map( ({key: keyChar}) => keyChar.length === 1 ? keyChar : `[${keyChar}]` ).join("")
							}</Literal>
					</div>)
				}</IndentedBlock>
			</>)}
			{ eventListenersOnGlobalTags == null ? null : (<>
				<ReportSubSubSectionHeader>Browser (DOM) events listened upon</ReportSubSubSectionHeader>
				<IndentedBlock>{
					[...eventListenersOnGlobalTags.values()].map( (event, index) => (
						<span key={event.domEventName}>{index === 0 ? null : (<>,&nbsp;</>)}
							<SizeAdjustedLowercaseLiteral>{event.domEventName}</SizeAdjustedLowercaseLiteral> ({ [...event.tags.values()].map( ({tag}, index) => (
								<span key={tag}>
									{index === 0 ? null : (<>,&nbsp;</>)}
									<Literal>{tag}</Literal>
								</span>
							))})
						</span>)
					)
				}</IndentedBlock>	
			</>)}
			{ globalPropertiesRead == null ? null : (<>
				<ReportSubSubSectionHeader>Browser (DOM) event properties read</ReportSubSubSectionHeader>
				<IndentedBlock>{ Object.entries(globalPropertiesRead).map( ([event, properties]) => 
					<IndentedBlock key={event}>
						<SizeAdjustedLowercaseLiteral>{event}</SizeAdjustedLowercaseLiteral>:&nbsp;
						{ Object.values(properties).map( ({property, tags}, index) => (
							<span key={property}>{index === 0 ? null : (<>,&nbsp;</>)}
								<Literal>{property}</Literal> ({ Object.values(tags).map( ({tag}, index) => (<span key={tag}>
									{index === 0 ? null : (<>,&nbsp;</>)}
									<Literal>{tag}</Literal>
								</span>))})
							</span>) ) }
					</IndentedBlock>)
				}</IndentedBlock>
			</>)}
		</IndentedBlock>
	</>);
});

export const CookieSubsectionView = observer( ({cookies}: {cookies?: {name: string, value: string}[]}) => {
	if (cookies == null || cookies.length === 0) return null;
	return (<>
		<ReportSubSectionHeader>Cookies</ReportSubSectionHeader>
			<IndentedBlock>{
			cookies.map( (cookie, index) => (
				<div key={cookie.name}><Literal>{cookie.name}={cookie.value}</Literal></div>
		))}</IndentedBlock>
	</>)
});



export const TrackingSummaryReportView = observer(
	({
		trackingSummaryReportState,
		displayJson,
	}: {
		trackingSummaryReportState: TrackingSummaryReportState;
		displayJson?: (json: unknown) => void;
	}) => {
		return (<>
			{trackingSummaryReportState.map( ({ sourceRegisteredDomain, resultsByDestinationRegisteredDomain }) => {		
				return (
					<Report key={sourceRegisteredDomain}>
						<ReportTitle>Tracking Activity on &#32;
							<SizeAdjustedLowercaseLiteral>{sourceRegisteredDomain}</SizeAdjustedLowercaseLiteral></ReportTitle>
						{resultsByDestinationRegisteredDomain.map(({
							cookies, destinationRegisteredDomain,
							searchResults, behaviorsObservedAtCodeLocation,
							knownTracker,
						}) => {
							const organizationKnownAs = knownTracker?.organizationKnownAs;
							return (
								<ReportSection key={`${destinationRegisteredDomain}\0${sourceRegisteredDomain}`}>
									<ReportSectionHeader>{ organizationKnownAs == null ? ( 
											<SizeAdjustedLowercaseLiteral>{destinationRegisteredDomain}</SizeAdjustedLowercaseLiteral>
										) : (<>
												{organizationKnownAs}&#32;
												<SizeAdjustedLowercaseLiteral>({destinationRegisteredDomain})</SizeAdjustedLowercaseLiteral>
										</>)
									}</ReportSectionHeader>
									<KnownTrackerView knownTracker={knownTracker} />
									<CookieSubsectionView cookies={cookies} />
									<DomainInstrumentationReport {...{behaviorsObservedAtCodeLocation}} />
									<DomainSearchResultsView displayJson={displayJson} searchTerms={searchResults?.searchTerms} />
								</ReportSection>
						)})}
					</Report>
				);
			})}
		</>);
	});

